import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject, takeUntil, forkJoin } from 'rxjs';
import { ActivityScheduleService } from '../../services/activity-schedule.service';
import { ScheduledActivity } from '../../interfaces/activity-schedule.interface';
import { AuthService } from 'src/app/services/auth.service';
import { NotificationService } from 'src/app/services/notification.service';
// import { ActivityExportService } from '../../services/activity-export.service'; // Will be implemented later

export interface ActivityReport {
  totalActivities: number;
  completedActivities: number;
  cancelledActivities: number;
  rescheduledActivities: number;
  noShowActivities: number;
  pendingActivities: number;
  averageDuration: number;
  totalHours: number;
  completionRate: number;
  onTimeRate: number;
  activityTypeBreakdown: { [type: string]: number };
  monthlyStats: { month: string; count: number; completed: number }[];
  weeklyStats: { week: string; count: number; completed: number }[];
}

export interface ActivityMetrics {
  period: string;
  activities: ScheduledActivity[];
  report: ActivityReport;
}

@Component({
  selector: 'app-activity-reports',
  templateUrl: './activity-reports.component.html',
  styleUrls: ['./activity-reports.component.scss']
})
export class ActivityReportsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  
  // State
  isLoading = false;
  isExporting = false;
  currentUserId = 1;
  
  // Forms
  filterForm!: FormGroup;
  exportForm!: FormGroup;
  
  // Data
  currentMetrics: ActivityMetrics | null = null;
  comparisonMetrics: ActivityMetrics | null = null;
  allActivities: ScheduledActivity[] = [];
  filteredActivities: ScheduledActivity[] = [];
  
  // Configuration
  periodOptions = [
    { value: 'week', label: 'Această săptămână' },
    { value: 'month', label: 'Luna aceasta' },
    { value: 'quarter', label: 'Trimestrul acesta' },
    { value: 'year', label: 'Anul acesta' },
    { value: 'custom', label: 'Perioadă personalizată' }
  ];
  
  reportTypeOptions = [
    { value: 'summary', label: 'Raport sumar', icon: 'assessment' },
    { value: 'detailed', label: 'Raport detaliat', icon: 'list_alt' },
    { value: 'performance', label: 'Analiză performanță', icon: 'trending_up' },
    { value: 'comparison', label: 'Comparație perioade', icon: 'compare' }
  ];
  
  exportFormatOptions = [
    { value: 'pdf', label: 'PDF', icon: 'picture_as_pdf' },
    { value: 'excel', label: 'Excel', icon: 'table_chart' },
    { value: 'csv', label: 'CSV', icon: 'description' }
  ];
  
  // Chart data
  activityTypeChartData: any;
  monthlyTrendChartData: any;
  statusDistributionChartData: any;
  performanceChartData: any;
  
  // Chart options
  chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      }
    }
  };

  constructor(
    private fb: FormBuilder,
    private activityScheduleService: ActivityScheduleService,
    private authService: AuthService,
    private notificationService: NotificationService
    // private exportService: ActivityExportService // Will be implemented later
  ) {}

  async ngOnInit(): Promise<void> {
    this.initializeForms();
    await this.initializeUser();
    this.loadInitialData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async initializeUser(): Promise<void> {
    try {
      const user = await this.authService.getCurrentUser();
      if (user && user.id_user) {
        this.currentUserId = user.id_user;
      }
    } catch (error) {
      console.error('Error loading current user:', error);
    }
  }

  private initializeForms(): void {
    this.filterForm = this.fb.group({
      period: ['month', Validators.required],
      startDate: [''],
      endDate: [''],
      activityType: ['all'],
      status: ['all'],
      compareWithPrevious: [false]
    });

    this.exportForm = this.fb.group({
      reportType: ['summary', Validators.required],
      format: ['pdf', Validators.required],
      includeCharts: [true],
      includeDetails: [true]
    });

    // Watch for period changes
    this.filterForm.get('period')?.valueChanges.subscribe(period => {
      this.updateDateRangeValidators(period);
    });
  }

  private updateDateRangeValidators(period: string): void {
    const startDateControl = this.filterForm.get('startDate');
    const endDateControl = this.filterForm.get('endDate');
    
    if (period === 'custom') {
      startDateControl?.setValidators([Validators.required]);
      endDateControl?.setValidators([Validators.required]);
    } else {
      startDateControl?.clearValidators();
      endDateControl?.clearValidators();
    }
    
    startDateControl?.updateValueAndValidity();
    endDateControl?.updateValueAndValidity();
  }

  private loadInitialData(): void {
    this.applyFilters();
  }

  applyFilters(): void {
    if (!this.filterForm.valid) {
      this.notificationService.warningSwal('Eroare', 'Vă rugăm să completați toate câmpurile obligatorii.', 'error');
      return;
    }

    this.isLoading = true;
    const formValue = this.filterForm.value;
    
    const { startDate, endDate } = this.calculateDateRange(formValue.period, formValue.startDate, formValue.endDate);
    
    // Load current period data
    this.activityScheduleService.getActivitiesByDateRange(
      this.currentUserId, 
      startDate, 
      endDate,
      formValue.activityType !== 'all' ? [formValue.activityType] : undefined,
      formValue.status !== 'all' ? [formValue.status] : undefined
    ).pipe(takeUntil(this.destroy$)).subscribe({
      next: (activities) => {
        this.allActivities = activities;
        this.filteredActivities = activities;
        this.currentMetrics = {
          period: this.formatPeriodLabel(formValue.period, startDate, endDate),
          activities: activities,
          report: this.calculateReport(activities)
        };
        
        // Load comparison data if requested
        if (formValue.compareWithPrevious) {
          this.loadComparisonData(formValue.period, startDate, endDate);
        } else {
          this.comparisonMetrics = null;
          this.generateChartData();
          this.isLoading = false;
        }
      },
      error: (error) => {
        console.error('Error loading activity data:', error);
        this.notificationService.warningSwal('Eroare', 'Eroare la încărcarea datelor', 'error');
        this.isLoading = false;
      }
    });
  }

  private loadComparisonData(period: string, currentStart: string, currentEnd: string): void {
    const { startDate: prevStart, endDate: prevEnd } = this.calculatePreviousPeriod(period, currentStart, currentEnd);
    
    this.activityScheduleService.getActivitiesByDateRange(this.currentUserId, prevStart, prevEnd)
      .pipe(takeUntil(this.destroy$)).subscribe({
        next: (activities) => {
          this.comparisonMetrics = {
            period: this.formatPeriodLabel('previous', prevStart, prevEnd),
            activities: activities,
            report: this.calculateReport(activities)
          };
          
          this.generateChartData();
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error loading comparison data:', error);
          this.comparisonMetrics = null;
          this.generateChartData();
          this.isLoading = false;
        }
      });
  }

  private calculateDateRange(period: string, customStart?: string, customEnd?: string): { startDate: string; endDate: string } {
    const now = new Date();
    let startDate: Date;
    let endDate: Date = new Date(now);

    switch (period) {
      case 'week':
        startDate = new Date(now);
        startDate.setDate(now.getDate() - now.getDay() + 1); // Monday
        break;
      case 'month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'quarter':
        const quarterStart = Math.floor(now.getMonth() / 3) * 3;
        startDate = new Date(now.getFullYear(), quarterStart, 1);
        break;
      case 'year':
        startDate = new Date(now.getFullYear(), 0, 1);
        break;
      case 'custom':
        startDate = new Date(customStart || now.toISOString().split('T')[0]);
        endDate = new Date(customEnd || now.toISOString().split('T')[0]);
        break;
      default:
        startDate = new Date(now);
        startDate.setDate(1); // First day of current month
    }

    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    };
  }

  private calculatePreviousPeriod(period: string, currentStart: string, currentEnd: string): { startDate: string; endDate: string } {
    const current = new Date(currentStart);
    const diff = new Date(currentEnd).getTime() - current.getTime();
    const daysDiff = Math.ceil(diff / (1000 * 3600 * 24));
    
    const prevEnd = new Date(current);
    prevEnd.setDate(prevEnd.getDate() - 1);
    
    const prevStart = new Date(prevEnd);
    prevStart.setDate(prevStart.getDate() - daysDiff + 1);
    
    return {
      startDate: prevStart.toISOString().split('T')[0],
      endDate: prevEnd.toISOString().split('T')[0]
    };
  }

  private calculateReport(activities: ScheduledActivity[]): ActivityReport {
    const total = activities.length;
    const completed = activities.filter(a => a.status === 'completed').length;
    const cancelled = activities.filter(a => a.status === 'cancelled').length;
    const rescheduled = activities.filter(a => a.status === 'rescheduled').length;
    const noShow = activities.filter(a => a.status === 'no_show').length;
    const pending = activities.filter(a => a.status === 'scheduled').length;
    
    // Calculate total hours
    let totalMinutes = 0;
    activities.forEach(activity => {
      if (activity.start_time && activity.end_time) {
        const start = this.timeToMinutes(activity.start_time);
        const end = this.timeToMinutes(activity.end_time);
        totalMinutes += (end - start);
      }
    });
    
    const totalHours = totalMinutes / 60;
    const averageDuration = total > 0 ? totalHours / total : 0;
    
    // Activity type breakdown
    const typeBreakdown: { [type: string]: number } = {};
    activities.forEach(activity => {
      const typeName = activity.activity_type?.name || 'Necunoscut';
      typeBreakdown[typeName] = (typeBreakdown[typeName] || 0) + 1;
    });
    
    // Monthly and weekly stats
    const monthlyStats = this.calculateMonthlyStats(activities);
    const weeklyStats = this.calculateWeeklyStats(activities);
    
    return {
      totalActivities: total,
      completedActivities: completed,
      cancelledActivities: cancelled,
      rescheduledActivities: rescheduled,
      noShowActivities: noShow,
      pendingActivities: pending,
      averageDuration: Math.round(averageDuration * 100) / 100,
      totalHours: Math.round(totalHours * 100) / 100,
      completionRate: total > 0 ? Math.round((completed / total) * 10000) / 100 : 0,
      onTimeRate: total > 0 ? Math.round(((completed + pending) / total) * 10000) / 100 : 0,
      activityTypeBreakdown: typeBreakdown,
      monthlyStats,
      weeklyStats
    };
  }

  private calculateMonthlyStats(activities: ScheduledActivity[]): { month: string; count: number; completed: number }[] {
    const monthlyData: { [key: string]: { count: number; completed: number } } = {};
    
    activities.forEach(activity => {
      const date = new Date(activity.scheduled_date);
      const monthKey = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;
      
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = { count: 0, completed: 0 };
      }
      
      monthlyData[monthKey].count++;
      if (activity.status === 'completed') {
        monthlyData[monthKey].completed++;
      }
    });
    
    return Object.keys(monthlyData).map(month => ({
      month,
      count: monthlyData[month].count,
      completed: monthlyData[month].completed
    })).sort((a, b) => a.month.localeCompare(b.month));
  }

  private calculateWeeklyStats(activities: ScheduledActivity[]): { week: string; count: number; completed: number }[] {
    const weeklyData: { [key: string]: { count: number; completed: number } } = {};
    
    activities.forEach(activity => {
      const date = new Date(activity.scheduled_date);
      const week = this.getWeekNumber(date);
      const weekKey = `${date.getFullYear()}-S${week}`;
      
      if (!weeklyData[weekKey]) {
        weeklyData[weekKey] = { count: 0, completed: 0 };
      }
      
      weeklyData[weekKey].count++;
      if (activity.status === 'completed') {
        weeklyData[weekKey].completed++;
      }
    });
    
    return Object.keys(weeklyData).map(week => ({
      week,
      count: weeklyData[week].count,
      completed: weeklyData[week].completed
    })).sort((a, b) => a.week.localeCompare(b.week));
  }

  private getWeekNumber(date: Date): number {
    const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
    const pastDaysOfYear = (date.getTime() - firstDayOfYear.getTime()) / 86400000;
    return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
  }

  private timeToMinutes(timeStr: string): number {
    if (!timeStr) return 0;
    const parts = timeStr.split(':');
    if (parts.length !== 2) return 0;
    const hours = parseInt(parts[0], 10) || 0;
    const minutes = parseInt(parts[1], 10) || 0;
    return hours * 60 + minutes;
  }

  private formatPeriodLabel(period: string, startDate: string, endDate: string): string {
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    const formatOptions: Intl.DateTimeFormatOptions = {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    };
    
    if (period === 'previous') {
      return `${start.toLocaleDateString('ro-RO', formatOptions)} - ${end.toLocaleDateString('ro-RO', formatOptions)} (perioada anterioară)`;
    }
    
    return `${start.toLocaleDateString('ro-RO', formatOptions)} - ${end.toLocaleDateString('ro-RO', formatOptions)}`;
  }

  private generateChartData(): void {
    if (!this.currentMetrics) return;
    
    const report = this.currentMetrics.report;
    
    // Activity type distribution
    this.activityTypeChartData = {
      labels: Object.keys(report.activityTypeBreakdown),
      datasets: [{
        data: Object.values(report.activityTypeBreakdown),
        backgroundColor: [
          '#FF6384',
          '#36A2EB',
          '#FFCE56',
          '#4BC0C0',
          '#9966FF',
          '#FF9F40'
        ]
      }]
    };
    
    // Status distribution
    this.statusDistributionChartData = {
      labels: ['Completate', 'Anulate', 'Reprogramate', 'Absențe', 'Programate'],
      datasets: [{
        data: [
          report.completedActivities,
          report.cancelledActivities,
          report.rescheduledActivities,
          report.noShowActivities,
          report.pendingActivities
        ],
        backgroundColor: ['#28a745', '#dc3545', '#ffc107', '#fd7e14', '#17a2b8']
      }]
    };
    
    // Monthly trend
    this.monthlyTrendChartData = {
      labels: report.monthlyStats.map(stat => {
        const [year, month] = stat.month.split('-');
        return new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString('ro-RO', {
          month: 'short',
          year: 'numeric'
        });
      }),
      datasets: [
        {
          label: 'Total activități',
          data: report.monthlyStats.map(stat => stat.count),
          borderColor: '#36A2EB',
          backgroundColor: 'rgba(54, 162, 235, 0.1)',
          fill: true
        },
        {
          label: 'Completate',
          data: report.monthlyStats.map(stat => stat.completed),
          borderColor: '#28a745',
          backgroundColor: 'rgba(40, 167, 69, 0.1)',
          fill: true
        }
      ]
    };
    
    // Performance comparison (if available)
    if (this.comparisonMetrics) {
      const currentReport = this.currentMetrics.report;
      const previousReport = this.comparisonMetrics.report;
      
      this.performanceChartData = {
        labels: ['Total', 'Completate', 'Rata completare (%)', 'Ore totale'],
        datasets: [
          {
            label: 'Perioada curentă',
            data: [
              currentReport.totalActivities,
              currentReport.completedActivities,
              currentReport.completionRate,
              currentReport.totalHours
            ],
            backgroundColor: 'rgba(54, 162, 235, 0.6)'
          },
          {
            label: 'Perioada anterioară',
            data: [
              previousReport.totalActivities,
              previousReport.completedActivities,
              previousReport.completionRate,
              previousReport.totalHours
            ],
            backgroundColor: 'rgba(255, 99, 132, 0.6)'
          }
        ]
      };
    }
  }

  exportReport(): void {
    if (!this.exportForm.valid || !this.currentMetrics) {
      this.notificationService.warningSwal('Eroare', 'Vă rugăm să selectați opțiunile de export.', 'error');
      return;
    }

    this.isExporting = true;
    const exportOptions = this.exportForm.value;
    
    const exportData = {
      reportType: exportOptions.reportType,
      currentMetrics: this.currentMetrics,
      comparisonMetrics: this.comparisonMetrics,
      chartData: exportOptions.includeCharts ? {
        activityTypeChart: this.activityTypeChartData,
        statusDistributionChart: this.statusDistributionChartData,
        monthlyTrendChart: this.monthlyTrendChartData,
        performanceChart: this.performanceChartData
      } : null,
      includeDetails: exportOptions.includeDetails,
      generatedBy: this.currentUserId,
      generatedAt: new Date().toISOString()
    };

    // Export functionality will be implemented later when export service is available
    this.isExporting = false;
    this.notificationService.warningSwal(
      'În dezvoltare', 
      'Funcționalitatea de export va fi disponibilă în curând.', 
      'info'
    );
  }

  resetFilters(): void {
    this.filterForm.reset({
      period: 'month',
      activityType: 'all',
      status: 'all',
      compareWithPrevious: false
    });
    this.applyFilters();
  }

  getPercentageChange(current: number, previous: number): number {
    if (previous === 0) return current > 0 ? 100 : 0;
    return Math.round(((current - previous) / previous) * 10000) / 100;
  }

  getChangeIcon(current: number, previous: number): string {
    const change = this.getPercentageChange(current, previous);
    return change > 0 ? 'trending_up' : change < 0 ? 'trending_down' : 'trending_flat';
  }

  getChangeColor(current: number, previous: number): string {
    const change = this.getPercentageChange(current, previous);
    return change > 0 ? 'text-success' : change < 0 ? 'text-danger' : 'text-muted';
  }

  refreshData(): void {
    this.applyFilters();
  }

  get hasData(): boolean {
    return this.currentMetrics !== null && this.currentMetrics.activities.length > 0;
  }

  get hasComparisonData(): boolean {
    return this.comparisonMetrics !== null;
  }
}